import { useEffect, useState } from 'react';
import { normalizeDegrees } from './geo';

export type HeadingSource = 'sim' | 'compass' | 'travel' | 'api' | 'none';

export interface HeadingReading {
  /** Degrees clockwise from true north, or null when nothing can say. */
  deg: number | null;
  source: HeadingSource;
}

interface WebkitOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
}

/**
 * Where the player is facing. Order of trust:
 * 1. a simulated heading from the URL (desk demo);
 * 2. the device compass through DeviceOrientationEvent (iOS gives
 *    webkitCompassHeading; other browsers give `alpha` when `absolute` is true);
 * 3. direction of travel from successive GPS fixes;
 * 4. the Geolocation API's own `coords.heading`, which is also travel-derived;
 * 5. nothing: the caller shows a north-up view and says so.
 *
 * On the glasses the geolocation comes from the paired phone (GDD section
 * 18), so a phone compass reports where the phone points, not the head.
 * Phone navigation opts into this compass; glasses navigation disables it
 * and retains travel heading. `pickHeading` selects the first available
 * source in the order above; it does not blend sensor readings.
 */
export function useHeading(
  sim: number | null,
  travelBearing: number | null,
  apiHeading: number | null,
  enabled = true
): HeadingReading {
  const [compass, setCompass] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || sim !== null) return;
    if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) return;
    let last = 0;
    const onOrient = (ev: DeviceOrientationEvent) => {
      const now = performance.now();
      // The sensor fires at 60 Hz; the display should not repaint that often.
      if (now - last < 250) return;
      last = now;
      const wk = (ev as WebkitOrientationEvent).webkitCompassHeading;
      if (typeof wk === 'number' && Number.isFinite(wk)) {
        setCompass(normalizeDegrees(wk));
      } else if (ev.absolute && typeof ev.alpha === 'number') {
        setCompass(normalizeDegrees(360 - ev.alpha));
      }
    };
    window.addEventListener('deviceorientationabsolute' as 'deviceorientation', onOrient);
    window.addEventListener('deviceorientation', onOrient);
    return () => {
      window.removeEventListener('deviceorientationabsolute' as 'deviceorientation', onOrient);
      window.removeEventListener('deviceorientation', onOrient);
    };
  }, [sim, enabled]);

  return pickHeading(sim, enabled ? compass : null, travelBearing, apiHeading);
}

export function pickHeading(
  sim: number | null,
  compass: number | null,
  travelBearing: number | null,
  apiHeading: number | null
): HeadingReading {
  if (sim !== null) return { deg: normalizeDegrees(sim), source: 'sim' };
  if (compass !== null) return { deg: compass, source: 'compass' };
  if (travelBearing !== null) return { deg: travelBearing, source: 'travel' };
  if (apiHeading !== null) return { deg: normalizeDegrees(apiHeading), source: 'api' };
  return { deg: null, source: 'none' };
}

/**
 * iOS 13+ needs a user gesture before the compass will report. Call this from
 * a pinch or key press; elsewhere it is a no-op that resolves true.
 */
export async function requestCompassPermission(): Promise<boolean> {
  const ctor = (globalThis as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } })
    .DeviceOrientationEvent;
  if (!ctor || typeof ctor.requestPermission !== 'function') return true;
  try {
    return (await ctor.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}
